import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Card, CardContent, useTheme, useMediaQuery, Chip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import facturaVentaService, { FacturaVenta, FacturaVentaDetalle } from '../../services/facturaVentaService';
import clienteService from '../../services/clienteService';
import productoService from '../../services/productoService';
import movMonedaService from '../../services/movMonedaService';
import pedidoVentaService from '../../services/pedidoVentaService';
import { transformarPedidoAFactura } from '../../utils/pedidoToFactura';
import { transformarCotizacionAFactura } from '../../utils/cotizacionToFactura';
import cotizacionVentaService from '../../services/cotizacionVentaService';
import FacturaForm from './components/FacturaForm';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import ProductoSearch from '../pedidoventa/components/ProductoSearch';

type FacturaVentaUI = FacturaVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string; tipoIVA?: string; dni?: string };
type FacturaVentaUIExt = FacturaVentaUI & { nroSuc?: number; cuit?: string; dni?: string; tipoIVA?: string; idCompania?: string };

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) {
    return '';
  }
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) {
    return '';
  }
  const strValue = String(rawValue).trim();
  if (!strValue || strValue === '0') {
    return '';
  }
  return strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) {
    return '';
  }
  const cleaned = String(valor).trim().toUpperCase();
  if (!cleaned) {
    return '';
  }
  const match = FORMAS_DE_PAGO_VALIDAS.find(forma => forma.toUpperCase() === cleaned);
  return match || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) {
    return 'EFECTIVO';
  }
  const rawValue = cliente.condVta ?? (cliente as any)?.condvta ?? null;
  return normalizarFormaDePago(rawValue);
};

const normalizarPorcentajeBonificacion = (valor?: number | string | null): number => {
  if (valor === null || valor === undefined) {
    return 0;
  }
  const numero = Number(valor);
  if (!Number.isFinite(numero)) {
    return 0;
  }
  return Math.max(0, Math.min(100, numero));
};


const NuevaFacturaVenta: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pedidoIdParam = searchParams.get('pedidoId');
  const cotizacionIdParam = searchParams.get('cotizacionId');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [loadingPedido, setLoadingPedido] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pedidoId, setPedidoId] = useState<number | null>(pedidoIdParam ? parseInt(pedidoIdParam, 10) : null);
  const [cotizacionId, setCotizacionId] = useState<number | null>(cotizacionIdParam ? parseInt(cotizacionIdParam, 10) : null);
  const [formaPagoDelPedido, setFormaPagoDelPedido] = useState<string | null>(null);
  const [transporteDelPedido, setTransporteDelPedido] = useState<string | null>(null); // Estado para transporte del pedido
  const [nombreDelPedido, setNombreDelPedido] = useState<string | null>(null); // Estado para nombre del pedido
  const [infoPedido, setInfoPedido] = useState<{ letra?: string; puntoVenta?: number; nroComprobante?: number; nroFormulario?: number } | null>(null);
  const [facturaVenta, setFacturaVenta] = useState<FacturaVentaUIExt>({
    nroFormulario: 0,
    nroTipoFormulario: '12',
    tipoCliente: 'C',
    fechaEmision: new Date(),
    horaEmision: new Date(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 0,
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: 0,
    cuit: '',
    dni: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });
  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [productoDialogOpen, setProductoDialogOpen] = useState(false);
  const [productos, setProductos] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroFacturaCreada, setNroFacturaCreada] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [cotizacionUSD, setCotizacionUSD] = useState<number | null>(null);
  const round2 = (n: number) => Math.round(n * 100) / 100;

  useEffect(() => {
    (async () => {
      try {
        const v = await movMonedaService.getUltimaCotizacion();
        if (v && Number.isFinite(v) && v > 0) {
          setCotizacionUSD(v);
        } else {
          setCotizacionUSD(null);
        }
      } catch {
        setCotizacionUSD(null);
      }
    })();
  }, []);

  // Cargar datos del pedido o cotización si existe pedidoId o cotizacionId
  useEffect(() => {
    const cargarDesdePedido = async () => {
      if (!pedidoId || isNaN(pedidoId)) return;
      try {
        setLoadingPedido(true);
        setError(null);
        const pedido = await pedidoVentaService.getPedidoVenta(pedidoId);
        const pedidoExt = pedido as any;
          
          // Guardar información del pedido para mostrar en el chip
          // El punto de venta es nroSuc, no puntoVenta
          setInfoPedido({
            letra: pedidoExt.letra || '',
            puntoVenta: pedidoExt.nroSuc || pedidoExt.puntoVenta || pedidoExt.puntoDeVenta || pedidoExt.nroPuntoVenta || 0,
            nroComprobante: pedidoExt.nroComprobante || pedidoExt.numeroComprobante || 0,
            nroFormulario: pedido.nroFormulario
          });
          
          // Transformar el pedido a factura
          const facturaTransformada = await transformarPedidoAFactura(pedido);
          
          // Pre-cargar todos los campos en el estado
          // La función transformarPedidoAFactura ya busca variaciones de nombres, pero verificamos también directamente del pedido
          
          // Guardar la forma de pago del pedido para que no se sobrescriba cuando se carga el cliente
          const formaPagoPedido = pedidoExt.formaDePago || facturaTransformada.formaDePago || '';
          if (formaPagoPedido) {
            setFormaPagoDelPedido(formaPagoPedido);
          }
          
          // Guardar el transporte del pedido para que no se sobrescriba cuando se carga el cliente
          const transportePedido = facturaTransformada.transporte || '';
          console.log('📦 Transporte del pedido transformado:', transportePedido);
          if (transportePedido) {
            setTransporteDelPedido(transportePedido);
            console.log('💾 Transporte guardado en estado transporteDelPedido:', transportePedido);
          }
          
          // Guardar el nombre del pedido para que no se sobrescriba cuando se carga el cliente
          const nombrePedido = facturaTransformada.nombre || pedido.nombre || '';
          if (nombrePedido) {
            setNombreDelPedido(nombrePedido);
            console.log('💾 Nombre del pedido guardado:', nombrePedido);
          }
          
          setFacturaVenta({
            ...facturaTransformada,
            ordenCompra: pedidoExt.ordenCompra || pedidoExt.ordenDeCompra || facturaTransformada.ordenCompra || '',
            transporte: transportePedido, // Usar el transporte de la transformación (ya busca nroExpreso)
            ubicacion: facturaTransformada.ubicacion || '',
            formaDePago: formaPagoPedido,
            nroSuc: facturaTransformada.nroSuc || 0,
            cuit: facturaTransformada.cuit || '',
            dni: (facturaTransformada as any).dni || '',
            tipoIVA: (facturaTransformada as any).tipoIVA || '',
            idCompania: facturaTransformada.idCompania || '',
          } as FacturaVentaUIExt);
          console.log('✅ facturaVenta actualizada con transporte:', transportePedido);
        } catch (err: any) {
          console.error('Error al cargar el pedido:', err);
          const message = err?.response?.data || 'Error al cargar el pedido para facturar';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setPedidoId(null);
        } finally {
          setLoadingPedido(false);
        }
    };

    const cargarDesdeCotizacion = async () => {
      if (!cotizacionId || isNaN(cotizacionId)) return;
      try {
        setLoadingPedido(true);
        setError(null);
        const cotizacion = await cotizacionVentaService.getCotizacionVenta(cotizacionId);
        const cotizacionExt = cotizacion as any;
        setInfoPedido({
          letra: cotizacionExt.letra || '',
          puntoVenta: cotizacionExt.nroSuc || 0,
          nroComprobante: cotizacionExt.nroComprobante || 0,
          nroFormulario: cotizacion.nroFormulario
        });
        const facturaTransformada = await transformarCotizacionAFactura(cotizacion);
        const formaPagoPedido = cotizacionExt.formaDePago || facturaTransformada.formaDePago || '';
        if (formaPagoPedido) setFormaPagoDelPedido(formaPagoPedido);
        const transportePedido = facturaTransformada.transporte || '';
        if (transportePedido) setTransporteDelPedido(transportePedido);
        const nombrePedido = facturaTransformada.nombre || cotizacion.nombre || '';
        if (nombrePedido) setNombreDelPedido(nombrePedido);
        setFacturaVenta({
          ...facturaTransformada,
          ordenCompra: cotizacionExt.ordenDeCompra || cotizacionExt.ordenCompra || facturaTransformada.ordenCompra || '',
          transporte: transportePedido,
          ubicacion: facturaTransformada.ubicacion || '',
          formaDePago: formaPagoPedido,
          nroSuc: facturaTransformada.nroSuc || 0,
          cuit: facturaTransformada.cuit || '',
          dni: (facturaTransformada as any).dni || '',
          tipoIVA: (facturaTransformada as any).tipoIVA || '',
          idCompania: facturaTransformada.idCompania || '',
        } as FacturaVentaUIExt);
      } catch (err: any) {
        console.error('Error al cargar la cotización:', err);
        const message = err?.response?.data || 'Error al cargar la cotización para facturar';
        setError(message);
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
        setCotizacionId(null);
      } finally {
        setLoadingPedido(false);
      }
    };

    if (pedidoId && !isNaN(pedidoId)) {
      cargarDesdePedido();
    } else if (cotizacionId && !isNaN(cotizacionId)) {
      cargarDesdeCotizacion();
    }
  }, [pedidoId, cotizacionId, enqueueSnackbar]);

  const handleProductoSearch = async () => {
    try {
      setLoading(true);
      const response = await productoService.getProductos({
        searchTerm,
        pageNumber: 1,
        pageSize: 50
      });
      setProductos(response.items);
    } catch (err: any) {
      enqueueSnackbar('Error al buscar productos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClienteSelect = (cliente: any) => {
    const transporteSugerido = obtenerTransporteSugerido(cliente);
    const formaPagoSugerida = obtenerFormaPagoSugerida(cliente);
    setFacturaVenta((prev: FacturaVentaUIExt) => ({
      ...prev,
      nroCliente: cliente.nroCliente ?? cliente.codigo ?? 0,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      // Respetar la forma de pago del pedido si existe, sino usar la del cliente
      formaDePago: formaPagoDelPedido || formaPagoSugerida,
      transporte: transporteSugerido,
      porcDesc: normalizarPorcentajeBonificacion(
        cliente.porcDesc ?? (cliente as any)?.porc_Desc ?? (cliente as any)?.porcdesc
      ),
      porcDesc2: normalizarPorcentajeBonificacion(
        cliente.porcDesc2 ?? (cliente as any)?.porc_Desc2 ?? (cliente as any)?.porcdesc2
      ),
      porcDesc3: normalizarPorcentajeBonificacion(
        cliente.porcDesc3 ?? (cliente as any)?.porc_Desc3 ?? (cliente as any)?.porcdesc3
      ),
    }));
    setClienteDialogOpen(false);
  };

  const handleProductoSelect = (producto: any) => {
    if (!facturaVenta.nroCliente || facturaVenta.nroCliente <= 0) {
      enqueueSnackbar('Debe seleccionar un cliente antes de agregar productos al carrito', { variant: 'warning' });
      return;
    }
    const base = Number(producto.precioVenta ?? producto.precio ?? producto.costo ?? producto.precioLista ?? producto.precioUnitario ?? 0);
    const tipoMoneda = producto.tipoMoneda ?? producto.tipoMonedaArticulo ?? 0;
    const baseRed = round2(base);
    const precio = (Number(tipoMoneda) === 1 && cotizacionUSD && cotizacionUSD > 0) ? round2(baseRed * cotizacionUSD) : baseRed;
    const nuevoDetalle: FacturaVentaDetalle = {
      nroIdformulario: facturaVenta.nroFormulario,
      nroItem: (facturaVenta.detalles?.length || 0) + 1,
      codigoArticulo: producto.codigoArticulo,
      descripcionArticulo: producto.descripcion,
      cantidad: 1,
      precioUnitario: precio,
      descuento: 0,
      subtotal: precio,
    };

    setFacturaVenta((prev: FacturaVentaUIExt) => ({
      ...prev,
      detalles: [...(prev.detalles || []), nuevoDetalle],
      importeTotal: (prev.importeTotal || 0) + precio,
    }));
    setProductoDialogOpen(false);
  };

  useEffect(() => {
    const v = Number(facturaVenta.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      try {
        localStorage.setItem('nroSuc', String(v));
        localStorage.setItem('sucursal', String(v));
      } catch {}
    }
  }, [facturaVenta.nroSuc]);

  useEffect(() => {
    const value = facturaVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro);
            const transporteSugerido = obtenerTransporteSugerido(c);
            const formaPagoSugerida = obtenerFormaPagoSugerida(c);
            setFacturaVenta(prev => {
              const transporteFinal = prev.transporte || transporteDelPedido || transporteSugerido;
              // Respetar el nombre del pedido si existe, sino usar el del cliente
              const nombreFinal = nombreDelPedido || prev.nombre || c.nombre;
              console.log('🔄 Cargando cliente - Transporte a usar:', {
                transporteEnFacturaVenta: prev.transporte,
                transporteDelPedido: transporteDelPedido,
                transporteSugerido: transporteSugerido,
                transporteFinal: transporteFinal
              });
              console.log('🔄 Cargando cliente - Nombre a usar:', {
                nombreDelPedido: nombreDelPedido,
                nombreEnFacturaVenta: prev.nombre,
                nombreDelCliente: c.nombre,
                nombreFinal: nombreFinal
              });
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: nombreFinal,
                cuit: c.cuit || '',
                dni: (c as any).dni || '',
                tipoIVA: c.tipoIva || '',
                // Respetar la forma de pago del pedido si existe, sino usar la del cliente
                formaDePago: formaPagoDelPedido || formaPagoSugerida,
                // Respetar el transporte del pedido: primero el que ya está en facturaVenta (del pedido),
                // luego el guardado en transporteDelPedido, y finalmente el del cliente
                transporte: transporteFinal,
                porcDesc: normalizarPorcentajeBonificacion(
                  c.porcDesc ?? (c as any)?.porc_Desc ?? (c as any)?.porcdesc
                ),
                porcDesc2: normalizarPorcentajeBonificacion(
                  c.porcDesc2 ?? (c as any)?.porc_Desc2 ?? (c as any)?.porcdesc2
                ),
                porcDesc3: normalizarPorcentajeBonificacion(
                  c.porcDesc3 ?? (c as any)?.porc_Desc3 ?? (c as any)?.porcdesc3
                ),
              };
            });
          } catch {}
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [facturaVenta.nroCliente, nombreDelPedido, transporteDelPedido, formaPagoDelPedido]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];

      if (!facturaVenta.nroCliente || facturaVenta.nroCliente <= 0) {
        erroresValidacion.push('Debe seleccionar un cliente');
      }

      if (!facturaVenta.fechaEmision) {
        erroresValidacion.push('Debe ingresar la Fecha de Emisión en la sección Información de la Factura');
      }

      const ubicacion = (facturaVenta as FacturaVentaUI).ubicacion;
      if (!ubicacion || ubicacion.trim() === '') {
        erroresValidacion.push('Debe seleccionar una Ubicación en la sección Información de la Factura');
      }

      const formaDePago = (facturaVenta as FacturaVentaUI).formaDePago;
      if (!formaDePago || formaDePago.trim() === '') {
        erroresValidacion.push('Debe seleccionar una Forma de Pago en la sección Información de la Factura');
      }

      if (!facturaVenta.detalles || facturaVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto a la factura');
      } else {
        facturaVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0) {
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          }
          if (!detalle.codigoArticulo) {
            erroresValidacion.push(`El item ${index + 1} no tiene código de artículo`);
          }
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((error) => {
          enqueueSnackbar(error, { variant: 'error', autoHideDuration: 5000 });
        });
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      const payload = {
        ...facturaVenta,
        nroSuc: facturaVenta.nroSuc || 0,
        ordenCompra: facturaVenta.ordenCompra || '',
        transporte: facturaVenta.transporte || '',
        ubicacion: facturaVenta.ubicacion || '',
        formaDePago: facturaVenta.formaDePago || '',
        fechaEmision: facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision : new Date(facturaVenta.fechaEmision),
        horaEmision: facturaVenta.horaEmision instanceof Date ? facturaVenta.horaEmision : (facturaVenta.horaEmision ? new Date(facturaVenta.horaEmision) : new Date()),
        nroFormularioPedido: pedidoId && !isNaN(pedidoId) ? pedidoId : undefined, // Solo para pedidos; cotización no vincula
      } as any;
      
      const response = await facturaVentaService.createFacturaVenta(payload);
      
      let nroFactura: number | null = null;
      if (response?.nroFormulario) {
        nroFactura = response.nroFormulario;
      } else if ((response as any)?.formulario?.nroFormulario) {
        nroFactura = (response as any).formulario.nroFormulario;
      } else if ((response as any)?.data?.nroFormulario) {
        nroFactura = (response as any).data.nroFormulario;
      }
      
      if (nroFactura) {
        try {
          const facturaCargada = await facturaVentaService.getFacturaVenta(nroFactura);
          
          // Procesar los detalles para usar precioLista y porcDesc del GET
          const detallesProcesados = (facturaCargada.detalles || []).map((detalle: any) => {
            // Usar precioLista y porcDesc directamente del GET del backend
            const precioLista = detalle.precioLista ?? detalle.precioUnitario ?? 0;
            const porcDesc = detalle.porcDesc ?? 0;
            
            return {
              ...detalle,
              precioLista: precioLista,
              precioUnitario: precioLista, // precioUnitario debe ser precioLista (sin descuento)
              porcDesc: porcDesc,
            };
          });
          
          setFacturaVenta({
            ...facturaCargada,
            detalles: detallesProcesados,
            ordenCompra: facturaVenta.ordenCompra || '',
            transporte: facturaVenta.transporte || '',
            ubicacion: facturaVenta.ubicacion || '',
            formaDePago: facturaVenta.formaDePago || '',
            nroSuc: facturaVenta.nroSuc || 0,
            cuit: facturaVenta.cuit || '',
            dni: facturaVenta.dni || '',
            tipoIVA: facturaVenta.tipoIVA || '',
            idCompania: facturaVenta.idCompania || '',
          } as FacturaVentaUIExt);

          setNroFacturaCreada(nroFactura);
          setSuccessDialogOpen(true);
          
          enqueueSnackbar(`Factura #${nroFactura} creada exitosamente`, { variant: 'success', autoHideDuration: 3000 });
        } catch (loadError: any) {
          console.error('Error al cargar la factura:', loadError);
          enqueueSnackbar('Factura creada exitosamente, pero no se pudo cargar la información completa', { variant: 'warning' });
          setFacturaVenta({
            nroFormulario: 0,
            nroTipoFormulario: '12',
            tipoCliente: 'C',
            fechaEmision: new Date(),
            horaEmision: new Date(),
            nroCliente: 0,
            nombre: '',
            nroVendedor: 0,
            nroLista: '',
            codigoMoneda: 0,
            importeTotal: 0,
            asentado: 'N',
            descripcion: '',
            usuario: '',
            ordenCompra: '',
            transporte: '',
            ubicacion: '',
            formaDePago: '',
            nroSuc: 0,
            cuit: '',
            dni: '',
            tipoIVA: '',
            idCompania: '',
            detalles: [],
          });
        }
      } else {
        enqueueSnackbar('Factura de venta guardada exitosamente', { variant: 'success' });
        setFacturaVenta({
          nroFormulario: 0,
          nroTipoFormulario: '12',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 0,
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          nroSuc: 0,
          cuit: '',
          dni: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
        });
      }
    } catch (err: any) {
      console.error('Error al guardar factura:', err);
      console.error('Error completo:', {
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        message: err?.message,
        response: err?.response
      });
      
      const status = err?.response?.status;
      const data = err?.response?.data;
      let errorMessage = 'Error al guardar la factura de venta';
      let mostrado = false;

      // Primero intentar capturar el mensaje directamente de la respuesta del backend
      // Esto captura errores como "No hay suficiente stock disponible..."
      if (data && typeof data === 'string') {
        errorMessage = data;
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
        setError(errorMessage);
        mostrado = true;
      } else if (data?.message && typeof data.message === 'string') {
        errorMessage = data.message;
        // Si el mensaje contiene "stock" o "disponible", mostrarlo directamente
        if (errorMessage.toLowerCase().includes('stock') || errorMessage.toLowerCase().includes('disponible')) {
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          setError(errorMessage);
          mostrado = true;
        }
      }

      if (err?.message && (!err?.response || err.message.includes('Se encontraron') || err.message.includes('obligatorio') || err.message.includes('obligatoria'))) {
        errorMessage = err.message;
        
        let errores: string[] = [];
        if (err.message.includes('Se encontraron')) {
          const partes = err.message.split('\n');
          errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter((e: string) => e);
        } else if (err.message.includes('\n')) {
          errores = err.message.split('\n').map((e: string) => e.trim()).filter((e: string) => e && !e.includes('Errores de validación'));
        } else {
          errores = [err.message];
        }
        
        errores.forEach((error: string) => {
          if (error.trim()) {
            enqueueSnackbar(error.trim(), { variant: 'error', autoHideDuration: 6000 });
          }
        });
        
        if (errores.length > 1) {
          setError(`Se encontraron ${errores.length} errores:\n${errores.map((e, i) => `${i + 1}. ${e}`).join('\n')}`);
        } else if (errores.length === 1) {
          setError(errores[0]);
        } else {
          setError(errorMessage);
        }
        mostrado = true;
      }
      else if (data?.errors && typeof data.errors === 'object') {
        try {
          const msgs: string[] = [];
          for (const k of Object.keys(data.errors)) {
            const val = data.errors[k];
            if (Array.isArray(val)) {
              msgs.push(`${k}: ${val.join(' - ')}`);
            } else if (val) {
              msgs.push(`${k}: ${String(val)}`);
            }
          }
          if (msgs.length > 0) {
            errorMessage = `Error de validación del servidor:\n${msgs.join('\n')}`;
            msgs.forEach((msg: string) => {
              enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
            });
            setError(errorMessage);
            mostrado = true;
          }
        } catch {}
      }
      else if (!mostrado) {
        if (status === 400) {
          // Intentar capturar el mensaje de diferentes formas
          errorMessage = data?.title || data?.message || data?.error || (typeof data === 'string' ? data : 'Solicitud inválida. Revise los datos ingresados.');
          
          // Si data es un string directo, usarlo
          if (typeof data === 'string' && data.trim()) {
            errorMessage = data.trim();
          } else if (data?.message) {
            errorMessage = data.message;
          } else if (data?.error) {
            errorMessage = data.error;
          }
          
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          }
          setError(errorMessage);
          mostrado = true;
        } else if (status === 401 || status === 403) {
          errorMessage = 'No autorizado para crear facturas. Verifique su sesión y permisos.';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          setError(errorMessage);
          mostrado = true;
        } else if (status === 404) {
          const urlIntentada = err?.config?.url || 'desconocida';
          errorMessage = `Endpoint no encontrado (404). URL intentada: ${urlIntentada}. Verifique que el endpoint esté disponible en el servidor.`;
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          setError(errorMessage);
          mostrado = true;
        } else if (status === 500) {
          errorMessage = data?.message || 'Error interno del servidor. Intente nuevamente o contacte con soporte.';
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          setError(errorMessage);
          mostrado = true;
        } else if (data?.message) {
          errorMessage = data.message;
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          }
          setError(errorMessage);
          mostrado = true;
        }
      }

      if (!mostrado) {
        const messageText = (data?.message || data || err?.message || '').toString();
        if (messageText && typeof messageText === 'string' && messageText.length > 0) {
          errorMessage = messageText.length > 200 ? messageText.substring(0, 200) + '...' : messageText;
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          }
          setError(errorMessage);
        } else {
          errorMessage = 'Error al guardar la factura de venta. Intente nuevamente.';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          setError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    navigate('/ventas/facturas');
  };

  // Handler para cambios en el formulario
  const handleFacturaChange = (factura: FacturaVenta) => {
    setFacturaVenta({
      ...facturaVenta,
      ...factura,
      // Preservar campos adicionales
      ordenCompra: (factura as FacturaVentaUI).ordenCompra ?? facturaVenta.ordenCompra ?? '',
      transporte: (factura as FacturaVentaUI).transporte ?? facturaVenta.transporte ?? '',
      ubicacion: (factura as FacturaVentaUI).ubicacion ?? facturaVenta.ubicacion ?? '',
      formaDePago: (factura as FacturaVentaUI).formaDePago ?? facturaVenta.formaDePago ?? '',
      nroSuc: (factura as FacturaVentaUI).nroSuc ?? facturaVenta.nroSuc ?? 0,
      cuit: (factura as FacturaVentaUI).cuit ?? facturaVenta.cuit ?? '',
      dni: (factura as FacturaVentaUI).dni ?? facturaVenta.dni ?? '',
      tipoIVA: (factura as FacturaVentaUI).tipoIVA ?? facturaVenta.tipoIVA ?? '',
      idCompania: (factura as any).idCompania ?? facturaVenta.idCompania ?? '',
    } as FacturaVentaUIExt);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Nueva Factura de Venta
            </Typography>
            {(pedidoId || cotizacionId) && infoPedido && (
              <Chip
                label={
                  infoPedido.letra && (infoPedido.puntoVenta ?? 0) > 0 && (infoPedido.nroComprobante ?? 0) > 0
                    ? `Facturando ${cotizacionId ? 'cotización' : 'pedido'} ${infoPedido.letra}-${String(infoPedido.puntoVenta ?? 0).padStart(5, '0')}-${String(infoPedido.nroComprobante ?? 0).padStart(8, '0')} (#${infoPedido.nroFormulario})`
                    : `Facturando ${cotizacionId ? 'cotización' : 'pedido'} #${infoPedido.nroFormulario || pedidoId || cotizacionId}`
                }
                color="info"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loadingPedido && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Cargando datos del pedido...
            </Alert>
          )}

          <FacturaForm
            facturaVenta={facturaVenta}
            onFacturaChange={handleFacturaChange}
            onClienteSearch={() => setClienteDialogOpen(true)}
            onProductoSearch={() => setProductoDialogOpen(true)}
            onSave={handleSave}
            loading={loading}
            saveButtonText="Guardar Factura"
          />
        </CardContent>
      </Card>

      <ClienteSearch
        open={clienteDialogOpen}
        onClose={() => setClienteDialogOpen(false)}
        onSelect={handleClienteSelect}
      />

      <ProductoSearch
        open={productoDialogOpen}
        onClose={() => setProductoDialogOpen(false)}
        onSearch={handleProductoSearch}
        onSelect={handleProductoSelect}
        productos={productos}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        loading={loading}
      />

      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 6,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.25rem',
            pb: 1.5,
            pt: 3,
            px: 3,
          }}
        >
          Factura Creada
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Factura creada exitosamente! Número de Factura: {nroFacturaCreada}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="text"
            color="primary"
            sx={{
              minWidth: 80,
              textTransform: 'none',
              fontWeight: 500,
              color: 'primary.main',
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NuevaFacturaVenta;

